/**
 * TrackResultPage — /prioritization/tracks/:trackId/result
 *
 * The result published for a track (the Published Track Result): one page
 * with its own address so Catchball, the cycle's results and the Plan of
 * Record can point at it. The panel is the same one the track page's Results
 * tab shows (one component, two places — build contract reuse rule).
 */
import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, ListOrdered } from 'lucide-react';
import { PageHeader } from '@/components/prioritization/PageHeader';
import { TrackResultPanel } from '@/components/prioritization/tracks/TrackResultPanel';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import { useItemLinkScope } from '@/lib/itemLinkScope';

export function TrackResultPage() {
  const { trackId = '' } = useParams<{ trackId: string }>();
  const tracks = useTrackStore((s) => s.tracks);
  const track = tracks.find((t) => t.id === trackId);
  useItemLinkScope({ cycleId: track?.cycleId });
  const fetchAllTracks = useTrackStore((s) => s.fetchAllTracks);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const fetchSessions = useSessionStore((s) => s.fetchFromSupabase);

  // A direct link or a refresh reconstructs everything from the database (§2.4).
  useEffect(() => {
    if (!track) void fetchAllTracks();
  }, [track, fetchAllTracks]);
  useEffect(() => {
    void fetchItems();
    void fetchSessions();
  }, [fetchItems, fetchSessions]);

  if (!trackId) return null;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/prioritization/tracks/${trackId}?tab=results`}
          className="inline-flex items-center text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-3"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to the track
        </Link>
        <PageHeader
          icon={<ListOrdered className="h-4 w-4 text-[var(--text-ter)]" />}
          title={`${track?.name ?? 'Track'} — Result`}
          subtitle="The result published for this track. It feeds the Plan of Record and what goes up through Catchball."
          mb="mb-0"
        />
      </div>
      <TrackResultPanel trackId={trackId} refreshKey={track?.updatedAt} />
    </div>
  );
}

export default TrackResultPage;
